import {
  experienceBucket,
  tokenize,
  yearsToBucket,
} from '../modules/dashboard/components/Jobs/ranking';
import {
  jobMatchesPreferredCity,
  parseLocationCities,
} from '../modules/dashboard/components/Jobs/jobPreferenceFilter';
import type { Job } from '../modules/dashboard/components/JobCard';
import type { JobMatchExplanation } from '../modules/dashboard/hooks/useJobMatchExplanation';
import type { UserProfile } from '../types/userProfile';

export type ApplyVerdict = 'yes_strong' | 'yes' | 'maybe' | 'no';
export type InterviewProbability = 'high' | 'medium' | 'low';

export interface ApplyRecommendation {
  verdict: ApplyVerdict;
  why: string[];
  concern: string | null;
  interviewProbability: InterviewProbability;
  recommendedAction: string;
  matchScore: number;
  skillsMatched: number;
  skillsRequired: number;
}

function getRequiredSkills(job: Job): string[] {
  const mustHave = job.must_have_skills ?? [];
  if (mustHave.length > 0) return mustHave;
  return (job.skills ?? []).slice(0, 10);
}

function buildUserSkillSet(profile: UserProfile | null | undefined): Set<string> {
  const skills = profile?.skills ?? [];
  const combined = tokenize(skills.join(' '));
  for (const skill of skills) {
    for (const token of tokenize(skill)) combined.add(token);
  }
  return combined;
}

function skillIsMatched(skill: string, userSkills: Set<string>): boolean {
  for (const token of tokenize(skill)) {
    if (userSkills.has(token)) return true;
  }
  return false;
}

function resolveSkillMatch(
  job: Job,
  profile: UserProfile | null | undefined,
  explanation?: JobMatchExplanation | null,
): { matched: string[]; missing: string[] } {
  const required = getRequiredSkills(job);
  const userSkills = buildUserSkillSet(profile);

  const preMatched = new Set<string>([
    ...(job.matchedSkills ?? []),
    ...(explanation?.matched_skills ?? []),
  ].map((s) => s.toLowerCase()));

  const matched: string[] = [];
  const missing: string[] = [];

  for (const skill of required) {
    const normalized = skill.toLowerCase();
    const found =
      preMatched.has(normalized)
      || skillIsMatched(skill, userSkills)
      || [...preMatched].some((m) => normalized.includes(m) || m.includes(normalized));

    if (found) matched.push(skill);
    else missing.push(skill);
  }

  if (required.length === 0 && explanation?.matched_skills?.length) {
    return {
      matched: explanation.matched_skills.slice(0, 6),
      missing: explanation.missing_skills.slice(0, 3),
    };
  }

  return { matched, missing };
}

function experienceMatches(
  profile: UserProfile | null | undefined,
  job: Job,
): boolean {
  const years = profile?.experienceYears;
  if (years == null) return false;

  const userBucket = yearsToBucket(years);
  const jobBucket =
    experienceBucket(job.experience_level)
    ?? experienceBucket(job.title)
    ?? experienceBucket(job.duration);

  if (jobBucket == null) return years >= 1;
  return Math.abs(userBucket - jobBucket) <= 1;
}

function locationMatches(
  profile: UserProfile | null | undefined,
  job: Job,
): boolean {
  if (job.remote) return true;

  const preferredCities = [
    ...parseLocationCities(profile?.location),
    ...(profile?.preferredLocations ?? []).flatMap((loc) => parseLocationCities(loc)),
  ];

  if (preferredCities.length > 0 && jobMatchesPreferredCity(job, preferredCities)) {
    return true;
  }

  const jobLoc = (job.location ?? '').toLowerCase();
  const userLoc = (profile?.location ?? '').toLowerCase();
  if (jobLoc && userLoc && (jobLoc.includes(userLoc) || userLoc.includes(jobLoc))) {
    return true;
  }

  return false;
}

function seniorityMatches(
  profile: UserProfile | null | undefined,
  job: Job,
): boolean {
  const years = profile?.experienceYears;
  if (years == null) return false;

  const userBucket = yearsToBucket(years);
  const jobBucket =
    experienceBucket(job.experience_level)
    ?? experienceBucket(job.title);

  if (jobBucket == null) return true;
  return Math.abs(userBucket - jobBucket) <= 1;
}

function resolveMatchScore(
  job: Job,
  skillRatio: number,
  explanation?: JobMatchExplanation | null,
): number {
  if (job.matchScore != null && job.matchScore > 0) return Math.round(job.matchScore);
  if (explanation?.score != null && explanation.score > 0) return Math.round(explanation.score);
  return Math.round(skillRatio * 100);
}

function resolveVerdict(score: number, skillRatio: number): ApplyVerdict {
  if (score >= 75 && skillRatio >= 0.75) return 'yes_strong';
  if (score >= 55 && skillRatio >= 0.5) return 'yes';
  if (score >= 35 || skillRatio >= 0.4) return 'maybe';
  return 'no';
}

function resolveInterviewProbability(
  score: number,
  missingCount: number,
): InterviewProbability {
  if (score >= 78 && missingCount <= 1) return 'high';
  if (score >= 58) return 'medium';
  return 'low';
}

function formatConcern(skill: string): string {
  const trimmed = skill.trim();
  if (!trimmed) return '';
  if (/experience$/i.test(trimmed)) return `${trimmed} not visible`;
  return `${trimmed} experience not visible`;
}

function buildRecommendedAction(
  verdict: ApplyVerdict,
  missing: string[],
  explanation?: JobMatchExplanation | null,
): string {
  const fromExplanation = explanation?.action_items?.find((item) => item?.trim());
  if (fromExplanation) return fromExplanation;

  if (missing.length > 0) {
    const skill = missing[0];
    return `Apply after adding ${skill} project experience.`;
  }

  if (verdict === 'yes_strong') {
    return 'Apply now — optimize your resume first for the best results.';
  }
  if (verdict === 'yes') {
    return 'Apply — highlight your strongest matching skills in your resume.';
  }
  if (verdict === 'maybe') {
    return 'Apply only if you can address the gaps in your resume or cover letter.';
  }
  return 'Focus on building the missing skills before applying to similar roles.';
}

export function computeApplyRecommendation(
  job: Job,
  profile?: UserProfile | null,
  explanation?: JobMatchExplanation | null,
): ApplyRecommendation {
  const { matched, missing } = resolveSkillMatch(job, profile, explanation);
  const requiredCount = Math.max(getRequiredSkills(job).length, matched.length + missing.length);
  const skillsMatched = matched.length;
  const skillsRequired = requiredCount > 0 ? requiredCount : Math.max(matched.length, 1);
  const skillRatio = skillsRequired > 0 ? skillsMatched / skillsRequired : 0.5;

  const why: string[] = [];

  if (skillsRequired > 0) {
    why.push(`${skillsMatched}/${skillsRequired} required skills`);
  }
  if (experienceMatches(profile, job)) {
    why.push('Experience matches');
  }
  if (locationMatches(profile, job)) {
    why.push('Location matches');
  }
  if (seniorityMatches(profile, job)) {
    why.push('Seniority matches');
  }

  if (why.length === 0) {
    why.push('Profile overlap detected');
  }

  const concernSkill =
    explanation?.missing_skills?.[0]
    ?? missing[0]
    ?? explanation?.skill_gaps?.[0]?.split(/[,;]/)[0]?.trim()
    ?? null;

  const concern = concernSkill ? formatConcern(concernSkill) : null;
  const matchScore = resolveMatchScore(job, skillRatio, explanation);
  const verdict = resolveVerdict(matchScore, skillRatio);

  return {
    verdict,
    why,
    concern,
    interviewProbability: resolveInterviewProbability(matchScore, missing.length),
    recommendedAction: buildRecommendedAction(verdict, missing, explanation),
    matchScore,
    skillsMatched,
    skillsRequired,
  };
}

/** Marketing demo — Senior Java @ Flipkart scenario. */
export function buildDemoApplyRecommendation(): ApplyRecommendation {
  return {
    verdict: 'yes_strong',
    why: [
      '5/6 required skills',
      'Experience matches',
      'Location matches',
      'Seniority matches',
    ],
    concern: 'AWS experience not visible',
    interviewProbability: 'high',
    recommendedAction: 'Apply after adding AWS project experience.',
    matchScore: 87,
    skillsMatched: 5,
    skillsRequired: 6,
  };
}
